import { CheckCircle2, ClipboardEdit } from 'lucide-react'
import type { QueueVisit, ReviewVisit, PastVisit } from '@/types/queue'
import { Badge } from '@/components/ui/badge'
import ClickableRow from './ClickableRow'
import { isToday, formatDateTime, visitTypeBadgeVariant, visitTypeLabel } from './utils'

function PatientInfo({ name, category }: { name: string; category: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-slate-900">{name}</span>
      <span className="text-xs text-slate-500">{category}</span>
    </div>
  )
}

export function VisitRow({ visit }: { visit: QueueVisit }) {
  const today = isToday(visit.scheduled_at)

  return (
    <ClickableRow
      to={`/visits/case/${visit.id}`}
      className={`hover:bg-slate-50 ${today ? 'border-primary-200 bg-primary-50/50' : 'bg-white'}`}
      aria-label={`Visit for ${visit.patient_name}`}
    >
      <PatientInfo name={visit.patient_name} category={visit.condition_category} />
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{formatDateTime(visit.scheduled_at)}</span>
        <Badge variant={visitTypeBadgeVariant(visit.visit_type)}>
          {visitTypeLabel(visit.visit_type)}
        </Badge>
      </div>
    </ClickableRow>
  )
}

export function ReviewVisitRow({ visit }: { visit: ReviewVisit }) {
  return (
    <ClickableRow
      to={`/visits/case/${visit.id}`}
      className="border-amber-200 bg-amber-50/50 hover:bg-amber-50"
      aria-label={`Visit for ${visit.patient_name}`}
    >
      <div className="flex items-center gap-3">
        <ClipboardEdit className="h-4 w-4 text-amber-500 shrink-0" />
        <PatientInfo name={visit.patient_name} category={visit.condition_category} />
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{formatDateTime(visit.visited_at)}</span>
        <Badge variant={visitTypeBadgeVariant(visit.visit_type)}>
          {visitTypeLabel(visit.visit_type)}
        </Badge>
      </div>
    </ClickableRow>
  )
}

export function PastVisitRow({ visit }: { visit: PastVisit }) {
  return (
    <ClickableRow
      to={`/visits/case/${visit.id}`}
      className="bg-white hover:bg-slate-50"
      aria-label={`Visit for ${visit.patient_name}`}
    >
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
        <PatientInfo name={visit.patient_name} category={visit.condition_category} />
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{formatDateTime(visit.completed_at)}</span>
        <Badge variant={visitTypeBadgeVariant(visit.visit_type)}>
          {visitTypeLabel(visit.visit_type)}
        </Badge>
      </div>
    </ClickableRow>
  )
}
